import { z } from "zod";
import { KwDataSchema } from "./ksiega-wieczysta.validator";
import { CONDITION_CATEGORIES } from "../valuation/index";

const ratingSchema = z.number().int().min(1).max(5);

/** Oceny 7 kategorii stanu — komplet kluczy wymuszony katalogiem z silnika wyceny. */
export const ConditionRatingsSchema = z.object(
  Object.fromEntries(CONDITION_CATEGORIES.map((c) => [c.id, ratingSchema])) as Record<
    (typeof CONDITION_CATEGORIES)[number]["id"],
    typeof ratingSchema
  >,
);

/** Wynik silnika wyceny zapisywany razem ze snapshotem — patrz OfferValuation.estimate. */
export const ValuationEstimateSchema = z.object({
  conditionScore: z.number(),
  conditionDelta: z.number(),
  conditionPpm: z.number(),
  floorAdjustment: z.number(),
  floorReason: z.string().max(200),
  finalPpm: z.number(),
  estimatedValue: z.number(),
  rangeLow: z.number(),
  rangeHigh: z.number(),
});

/**
 * Snapshot wyceny (RCN) — walidacja przy zapisie do projektu.
 * Pola silnika są opcjonalne: kreator zapisuje samą bazę RCN, zanim użytkownik
 * dojdzie do suwaków stanu, a oferty sprzed tej zmiany nie mają ich wcale.
 */
export const OfferValuationSchema = z.object({
  medianaPpm: z.number().nullable(),
  p25: z.number().nullable(),
  p75: z.number().nullable(),
  count: z.number().int().nonnegative(),
  typ: z.string().max(20),
  city: z.string().max(120),
  radiusM: z.number().int().positive(),
  fetchedAt: z.number(),
  p10: z.number().nullable().optional(),
  p90: z.number().nullable().optional(),
  ratings: ConditionRatingsSchema.nullable().optional(),
  floor: z.number().int().min(-5).max(200).nullable().optional(),
  hasElevator: z.boolean().nullable().optional(),
  areaM2: z.number().positive().max(100000).nullable().optional(),
  estimate: ValuationEstimateSchema.nullable().optional(),
  rows: z
    .array(
      z.object({
        address: z.string().max(255),
        areaM2: z.number().nullable(),
        price: z.number().nullable(),
        ppm: z.number().nullable(),
        market: z.string().max(30),
        date: z.string().max(10),
      }),
    )
    .max(50)
    .nullable()
    .optional(),
});

/**
 * PATCH /v1/projects/:id — częściowa aktualizacja pól oferty. Wszystkie pola opcjonalne
 * (kreator zapisuje krok po kroku). `null` czyści pole; brak klucza = bez zmian.
 */
export const UpdateOfferSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  address: z.string().trim().max(255).nullable().optional(),
  propertyType: z.enum(["lokale", "dzialki", "budynki"]).nullable().optional(),
  city: z.string().trim().max(120).nullable().optional(),
  street: z.string().trim().max(120).nullable().optional(),
  areaM2: z.number().positive().max(100000).nullable().optional(),
  rooms: z.number().int().min(0).max(100).nullable().optional(),
  floor: z.number().int().min(-5).max(200).nullable().optional(),
  price: z.number().nonnegative().max(1e12).nullable().optional(),
  condition: z.string().trim().max(60).nullable().optional(),
  description: z.string().max(8000).nullable().optional(),
  valuation: OfferValuationSchema.nullable().optional(),
  kwNumber: z.string().trim().max(20).nullable().optional(),
  kwData: KwDataSchema.nullable().optional(),
  status: z.enum(["draft", "ready"]).optional(),
  wizardStep: z.number().int().min(1).max(4).optional(),
  cardLayout: z.enum(["klasyczny", "duze-zdjecie", "dwie-kolumny", "galeria"]).nullable().optional(),
});

export type UpdateOfferInput = z.infer<typeof UpdateOfferSchema>;
